import { EFinancingProgram } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { CreditApplication, FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'
import { buildUrlWithIds } from '../make-api'
import { transformCreditApplicationFromApi } from '../credit-application/credit-transform'

const baseUrl = '/Applications/{financingProgramId}/{creditApplicationId}/loan'
const baseUrlV2 = '/Applications/{financingProgramId}/{creditApplicationId}/loanV2'

// Types
export interface CreateLoanDto {
  id: string
  financingProgramId: EFinancingProgram
}

// API Functions
export async function createLoan(data: CreateLoanDto): Promise<CreditApplication> {
  const apiClient = getApiClient()
  const url = buildUrlWithIds(baseUrl, { financingProgramId: data.financingProgramId, creditApplicationId: data.id })
  const response = await apiClient.post<CreditApplication>(url, data)
  return response.data
}

export async function createLoanV2(data: CreateLoanDto): Promise<CreditApplication> {
  const apiClient = getApiClient()
  const url = buildUrlWithIds(baseUrlV2, { financingProgramId: data.financingProgramId, creditApplicationId: data.id })
  const response = await apiClient.post<CreditApplication>(url, data)
  return response.data
}

// React-Query Hooks
export function useCreateLoan(): [MutationFunction<CreditApplication, CreateLoanDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation<CreditApplication, Error, CreateLoanDto>({
    mutationFn: createLoan,
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.id, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: transformCreditApplicationFromApi(updatedCredit),
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useCreateLoanV2(): [MutationFunction<CreditApplication, CreateLoanDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation<CreditApplication, Error, CreateLoanDto>({
    mutationFn: createLoanV2,
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.id, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: transformCreditApplicationFromApi(updatedCredit),
        })
      }
    },
  })

  return [mutateAsync, isPending]
}
